import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";

interface Chapter {
  id: string;
  class: string;
  chapterName: string;
}

interface Topic {
  id: string;
  class: string;
  chapterId: string;
  topicName: string;
}

const classOptions = ["IX", "X", "XI", "XII"];

const TopicManagement = () => {
  // Chapter state
  const [chapterName, setChapterName] = useState("");
  const [chapterClass, setChapterClass] = useState("");
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterEditId, setChapterEditId] = useState<string | null>(null);
  const [editChapterName, setEditChapterName] = useState("");
  const [editChapterClass, setEditChapterClass] = useState("");

  // Topic state
  const [topicName, setTopicName] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicEditId, setTopicEditId] = useState<string | null>(null);
  const [editTopicClass, setEditTopicClass] = useState("");
  const [editTopicChapterId, setEditTopicChapterId] = useState("");
  const [editTopicName, setEditTopicName] = useState("");

  // UI state
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChapters();
    fetchTopics();
  }, []);

const fetchChapters = async () => {
  try {
    const q = query(collection(db, "chapters"), orderBy("class"));
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as { class: string; chapterName: string })
    }));
    setChapters(data);
  } catch (err) {
    setError("Failed to fetch chapters.");
  }
};

const fetchTopics = async () => {
  setLoading(true);
  try {
    const q = query(collection(db, "topics"), orderBy("class"));
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as { class: string; chapterId: string; topicName: string })
    }));
    setTopics(data);
  } catch (err) {
    setError("Failed to fetch topics.");
  }
  setLoading(false);
};

// Helper: Map chapterId to chapter
const chapterMap: { [id: string]: Chapter } = {};
chapters.forEach((ch) => {
  chapterMap[ch.id] = ch;
});
// Helper: Group topics by class and chapter
const topicsGrouped: { [className: string]: { [chapterId: string]: Topic[] } } = {};
topics.forEach((topic) => {
  if (!topicsGrouped[topic.class]) topicsGrouped[topic.class] = {};
  if (!topicsGrouped[topic.class][topic.chapterId]) topicsGrouped[topic.class][topic.chapterId] = [];
  topicsGrouped[topic.class][topic.chapterId].push(topic);
});

  // Chapter CRUD
const handleChapterSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setSuccess("");
  if (!chapterClass || !chapterName) {
    setError("Please select a class and enter a chapter name.");
    return;
  }
  try {
    await addDoc(collection(db, "chapters"), {
      class: chapterClass,
      chapterName,
      createdAt: new Date(),
    });
    setSuccess("Chapter added successfully!");
    setChapterClass("");
    setChapterName("");
    setShowChapterForm(false);
    fetchChapters();
  } catch (err) {
    setError("Failed to add chapter.");
  }
};

const handleChapterDelete = async (id: string) => {
  if (!window.confirm("Are you sure you want to delete this chapter? Topics under this chapter will not be deleted automatically.")) return;
  try {
    await deleteDoc(doc(db, "chapters", id));
    fetchChapters();
  } catch (err) {
    setError("Failed to delete chapter.");
  }
};

const handleChapterEdit = (ch: Chapter) => {
  setChapterEditId(ch.id);
  setEditChapterClass(ch.class);
  setEditChapterName(ch.chapterName);
  setShowChapterForm(false);
  setError("");
  setSuccess("");
};
const handleChapterEditSave = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!editChapterClass || !editChapterName) {
    setError("Please select a class and enter a chapter name.");
    return;
  }
  try {
    await updateDoc(doc(db, "chapters", chapterEditId!), {
      class: editChapterClass,
      chapterName: editChapterName,
    });
    setChapterEditId(null);
    setEditChapterClass("");
    setEditChapterName("");
    setSuccess("Chapter updated successfully!");
    fetchChapters();
  } catch (err) {
    setError("Failed to update chapter.");
  }
};
const handleChapterEditCancel = () => {
  setChapterEditId(null);
  setEditChapterClass("");
  setEditChapterName("");
  setError("");
  setSuccess("");
};

// Topic CRUD
const handleTopicSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setSuccess("");
  if (!selectedClass || !selectedChapterId || !topicName) {
    setError("Please select a class, chapter, and enter a topic name.");
    return;
  }
  try {
    await addDoc(collection(db, "topics"), {
      class: selectedClass,
      chapterId: selectedChapterId,
      topicName,
      createdAt: new Date(),
    });
    setSuccess("Topic added successfully!");
    setTopicName("");
    setSelectedClass("");
    setSelectedChapterId("");
    setShowTopicForm(false);
    fetchTopics();
  } catch (err) {
    setError("Failed to add topic.");
  }
};
const handleTopicDelete = async (id: string) => {
  if (!window.confirm("Are you sure you want to delete this topic?")) return;
  try {
    await deleteDoc(doc(db, "topics", id));
    fetchTopics();
  } catch (err) {
    setError("Failed to delete topic.");
  }
};
const handleTopicEdit = (topic: Topic) => {
  setTopicEditId(topic.id);
  setEditTopicClass(topic.class);
  setEditTopicChapterId(topic.chapterId);
  setEditTopicName(topic.topicName);
  setShowTopicForm(false);
  setError("");
  setSuccess("");
};
const handleTopicEditSave = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!editTopicClass || !editTopicChapterId || !editTopicName) {
    setError("Please select a class, chapter, and enter a topic name.");
    return;
  }
  try {
    await updateDoc(doc(db, "topics", topicEditId!), {
      class: editTopicClass,
      chapterId: editTopicChapterId,
      topicName: editTopicName,
    });
    setTopicEditId(null);
    setEditTopicClass("");
    setEditTopicChapterId("");
    setEditTopicName("");
    setSuccess("Topic updated successfully!");
    fetchTopics();
  } catch (err) {
    setError("Failed to update topic.");
  }
};
const handleTopicEditCancel = () => {
  setTopicEditId(null);
  setEditTopicClass("");
  setEditTopicChapterId("");
  setEditTopicName("");
  setError("");
  setSuccess("");
};

return (
  <div className="max-w-3xl mx-auto bg-white p-6 rounded shadow mt-8">
    <h2 className="text-xl font-bold mb-4">Topic & Chapter Management</h2>

    {/* Success/Error Messages */}
    {success && <div className="mb-2 p-2 bg-green-100 text-green-700 rounded">{success}</div>}
    {error && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{error}</div>}

    {/* Chapter Management Section */}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-lg">Chapters</h3>
        <button
          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700"
          onClick={() => setShowChapterForm((prev) => !prev)}
        >
          {showChapterForm ? "Hide" : "Add Chapter"}
        </button>
      </div>
      {showChapterForm && (
        <form onSubmit={handleChapterSubmit} className="flex flex-col md:flex-row gap-2 mb-4">
          <select
            className="border px-2 py-1 rounded"
            value={chapterClass}
            onChange={e => setChapterClass(e.target.value)}
            required
          >
            <option value="">Select Class</option>
            {classOptions.map((cls) => (
              <option key={cls} value={cls}>{`Class ${cls}`}</option>
            ))}
          </select>
          <input
            type="text"
            className="border px-2 py-1 rounded"
            placeholder="Chapter Name"
            value={chapterName}
            onChange={e => setChapterName(e.target.value)}
            required
          />
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            Add
          </button>
        </form>
      )}
      {/* Chapter List */}
      <table className="min-w-full border text-sm mb-2">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2">Class</th>
            <th className="border px-4 py-2">Chapter Name</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {chapters.length === 0 ? (
            <tr><td colSpan={3} className="text-center">No chapters found.</td></tr>
          ) : (
            chapters.map((ch) => (
              <tr key={ch.id}>
                {chapterEditId === ch.id ? (
                  <>
                    <td className="border px-4 py-2">
                      <select
                        className="border px-2 py-1 rounded"
                        value={editChapterClass}
                        onChange={e => setEditChapterClass(e.target.value)}
                        required
                      >
                        <option value="">Select Class</option>
                        {classOptions.map((cls) => (
                          <option key={cls} value={cls}>{`Class ${cls}`}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border px-4 py-2">
                      <input
                        type="text"
                        className="border px-2 py-1 rounded"
                        value={editChapterName}
                        onChange={e => setEditChapterName(e.target.value)}
                        required
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <button className="bg-green-600 text-white px-2 py-1 rounded mr-2 hover:bg-green-700" onClick={handleChapterEditSave}>Save</button>
                      <button className="bg-gray-400 text-white px-2 py-1 rounded hover:bg-gray-500" onClick={handleChapterEditCancel}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="border px-4 py-2">Class {ch.class}</td>
                    <td className="border px-4 py-2">{ch.chapterName}</td>
                    <td className="border px-4 py-2">
                      <button className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600" onClick={() => handleChapterEdit(ch)}>Edit</button>
                      <button className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700" onClick={() => handleChapterDelete(ch.id)}>Delete</button>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    {/* Topic Management Section */}
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-lg">Topics</h3>
        <button
          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700"
          onClick={() => setShowTopicForm((prev) => !prev)}
        >
          {showTopicForm ? "Hide" : "Add Topic"}
        </button>
      </div>
      {showTopicForm && (
        <form onSubmit={handleTopicSubmit} className="flex flex-col md:flex-row gap-2 mb-4">
          <select
            className="border px-2 py-1 rounded"
            value={selectedClass}
            onChange={e => {
              setSelectedClass(e.target.value);
              setSelectedChapterId("");
            }}
            required
          >
            <option value="">Select Class</option>
            {classOptions.map((cls) => (
              <option key={cls} value={cls}>{`Class ${cls}`}</option>
            ))}
          </select>
          <select
            className="border px-2 py-1 rounded"
            value={selectedChapterId}
            onChange={e => setSelectedChapterId(e.target.value)}
            required
            disabled={!selectedClass}
          >
            <option value="">Select Chapter</option>
            {chapters.filter(ch => ch.class === selectedClass).map((ch) => (
              <option key={ch.id} value={ch.id}>{ch.chapterName}</option>
            ))}
          </select>
          <input
            type="text"
            className="border px-2 py-1 rounded"
            placeholder="Topic Name"
            value={topicName}
            onChange={e => setTopicName(e.target.value)}
            required
          />
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            Add
          </button>
        </form>
      )}
      {loading ? (
        <div>Loading topics...</div>
      ) : (
        Object.keys(topicsGrouped).length === 0 ? (
          <div>No topics found.</div>
        ) : (
          Object.keys(topicsGrouped).sort().map((cls) => (
            <div key={cls} className="mb-6">
              <h4 className="font-semibold text-blue-700 mb-2">Class {cls}</h4>
              {Object.keys(topicsGrouped[cls]).length === 0 ? (
                <div className="text-gray-500 mb-2">No topics for this class.</div>
              ) : (
                Object.keys(topicsGrouped[cls]).map((chapterId) => (
                  <div key={chapterId} className="mb-2">
                    <div className="font-semibold text-gray-700 mb-1">Chapter: {chapterMap[chapterId]?.chapterName || "[Deleted]"}</div>
                    <table className="min-w-full border text-sm mb-2">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border px-4 py-2">Topic Name</th>
                          <th className="border px-4 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topicsGrouped[cls][chapterId].map((topic) => (
                          <tr key={topic.id}>
                            {topicEditId === topic.id ? (
                              <>
                                <td className="border px-4 py-2">
                                  <form onSubmit={handleTopicEditSave} className="flex flex-col md:flex-row gap-2">
                                    <select
                                      className="border px-2 py-1 rounded"
                                      value={editTopicClass}
                                      onChange={e => setEditTopicClass(e.target.value)}
                                      required
                                    >
                                      <option value="">Select Class</option>
                                      {classOptions.map((cls) => (
                                        <option key={cls} value={cls}>{`Class ${cls}`}</option>
                                      ))}
                                    </select>
                                    <select
                                      className="border px-2 py-1 rounded"
                                      value={editTopicChapterId}
                                      onChange={e => setEditTopicChapterId(e.target.value)}
                                      required
                                    >
                                      <option value="">Select Chapter</option>
                                      {chapters.filter(ch => ch.class === editTopicClass).map((ch) => (
                                        <option key={ch.id} value={ch.id}>{ch.chapterName}</option>
                                      ))}
                                    </select>
                                    <input
                                      type="text"
                                      className="border px-2 py-1 rounded"
                                      value={editTopicName}
                                      onChange={e => setEditTopicName(e.target.value)}
                                      required
                                    />
                                  </form>
                                </td>
                                <td className="border px-4 py-2">
                                  <button className="bg-green-600 text-white px-2 py-1 rounded mr-2 hover:bg-green-700" onClick={handleTopicEditSave}>Save</button>
                                  <button className="bg-gray-400 text-white px-2 py-1 rounded hover:bg-gray-500" onClick={handleTopicEditCancel}>Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="border px-4 py-2">{topic.topicName}</td>
                                <td className="border px-4 py-2">
                                  <button className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600" onClick={() => handleTopicEdit(topic)}>Edit</button>
                                  <button className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700" onClick={() => handleTopicDelete(topic.id)}>Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          ))
        )
      )}
    </div>
  </div>
);

}

export default TopicManagement;
